import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis } from 'recharts';
import { api, qs } from '@/lib/api';
import type { Comparison, Store } from '@/lib/types';
import { fmtDate } from '@/lib/utils';
import { Card, CardHeader, Empty, ErrorNote, Input, Loading, PageHeader, Select } from '@/components/ui';

// Validated pair (dataviz validator: CVD ΔE ≥ 30, normal-vision ΔE 40, contrast ≥ 3:1 on white).
const OFFICIAL = '#1d4ed8';
const PRACTICE = '#d97706';
const AXIS = { fontSize: 12, fill: 'hsl(215 16% 42%)' };
// Legend labels stay in text ink; the swatch carries the series colour.
const legendText = (v: string) => <span style={{ color: 'hsl(215 28% 17%)' }}>{v}</span>;
const DAY = 86_400_000;

function Gap({ v }: { v: number | null }) {
  if (v === null) return <span className="text-muted-fg">—</span>;
  const text = v === 0 ? 'even' : `${v > 0 ? '+' : ''}${v} pts`;
  return <span className="tabular font-medium">{text}</span>;
}

export function ComparePage() {
  const [storeId, setStoreId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/api/stores') });
  const q = useQuery({
    queryKey: ['compare', storeId, from, to],
    queryFn: () => api.get<Comparison>(`/api/dashboard/practice-vs-official${qs({ store_id: storeId, from, to })}`),
  });
  const d = q.data;
  const points = (mode: 'practice' | 'official') => (d?.timeline ?? []).filter((t) => t.mode === mode).map((t) => ({ x: Date.parse(`${t.audit_date}T12:00:00Z`), y: t.score ?? 0, store: t.store_name, date: t.audit_date }));

  return (
    <>
      <PageHeader title="Practice vs official" subtitle="Do practice audits predict the real thing? A positive gap means practice scores run higher than official ones." />
      <Card className="mb-5 grid gap-3 p-3 sm:grid-cols-3">
        <Select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
          <option value="">All stores</option>
          {stores.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
      </Card>

      {q.isLoading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : !d || d.timeline.length === 0 ? (
        <Card><Empty title="No completed audits in this range.">Finish a practice and an official audit to see the comparison.</Empty></Card>
      ) : (
        <div className="space-y-5">
          <Card>
            <CardHeader title="Average score by store" subtitle="Completed, non-archived audits" />
            <div className="h-72 p-3">
              <ResponsiveContainer>
                <BarChart data={d.stores} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barGap={2}>
                  <CartesianGrid vertical={false} stroke="hsl(220 13% 92%)" />
                  <XAxis dataKey="store_name" tick={AXIS} tickLine={false} axisLine={false} interval={0} />
                  <YAxis domain={[0, 100]} tick={AXIS} tickLine={false} axisLine={false} unit="%" />
                  <Tooltip cursor={{ fill: 'hsl(220 14% 96%)' }} formatter={(v) => (v === null ? '—' : `${v}%`)} />
                  <Legend iconType="square" wrapperStyle={{ fontSize: 13 }} formatter={legendText} />
                  <Bar dataKey="official_avg" name="Official" fill={OFFICIAL} radius={[4, 4, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="practice_avg" name="Practice" fill={PRACTICE} radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto border-t">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-fg uppercase"><tr className="border-b"><th className="px-4 py-2 font-medium">Store</th><th className="px-4 py-2 font-medium">Official avg</th><th className="px-4 py-2 font-medium">Practice avg</th><th className="px-4 py-2 font-medium">Gap</th></tr></thead>
                <tbody>
                  {d.stores.map((s) => (
                    <tr key={s.store_id} className="border-b last:border-0">
                      <td className="px-4 py-2">{s.store_name}</td>
                      <td className="tabular px-4 py-2">{s.official_avg ?? '—'}{s.official_avg !== null && '%'} <span className="text-xs text-muted-fg">({s.official_count})</span></td>
                      <td className="tabular px-4 py-2">{s.practice_avg ?? '—'}{s.practice_avg !== null && '%'} <span className="text-xs text-muted-fg">({s.practice_count})</span></td>
                      <td className="px-4 py-2"><Gap v={s.gap} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader title="By section" subtitle="Where practice and official disagree most" />
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-fg uppercase"><tr className="border-b"><th className="px-4 py-2 font-medium">Section</th><th className="px-4 py-2 font-medium">Official</th><th className="px-4 py-2 font-medium">Practice</th><th className="px-4 py-2 font-medium">Gap</th></tr></thead>
                <tbody>
                  {[...d.sections].sort((a, b) => Math.abs(b.gap ?? 0) - Math.abs(a.gap ?? 0)).map((s) => (
                    <tr key={s.section_key} className="border-b last:border-0">
                      <td className="px-4 py-2">{s.label}</td>
                      <td className="tabular px-4 py-2">{s.official_avg ?? '—'}{s.official_avg !== null && '%'}</td>
                      <td className="tabular px-4 py-2">{s.practice_avg ?? '—'}{s.practice_avg !== null && '%'}</td>
                      <td className="px-4 py-2"><Gap v={s.gap} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <Card>
              <CardHeader title="Every audit over time" />
              <div className="h-72 p-3">
                <ResponsiveContainer>
                  <ScatterChart margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid stroke="hsl(220 13% 92%)" />
                    <XAxis type="number" dataKey="x" domain={[(min: number) => min - 2 * DAY, (max: number) => max + 2 * DAY]} scale="time" tick={AXIS} tickLine={false} axisLine={false} tickFormatter={(v: number) => fmtDate(new Date(v).toISOString(), true)} />
                    <YAxis type="number" dataKey="y" domain={[0, 100]} tick={AXIS} tickLine={false} axisLine={false} unit="%" />
                    <ZAxis range={[80, 80]} />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0].payload as { y: number; store: string; date: string };
                        return <div className="rounded-lg border bg-card px-3 py-2 text-xs shadow-md"><p className="font-medium">{p.store}</p><p>{fmtDate(p.date)} · {payload[0].name}: <strong>{p.y}%</strong></p></div>;
                      }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 13 }} formatter={legendText} />
                    <Scatter name="Official" data={points('official')} fill={OFFICIAL} stroke="#fff" strokeWidth={2} />
                    <Scatter name="Practice" data={points('practice')} fill={PRACTICE} stroke="#fff" strokeWidth={2} />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
